import React, { useState } from 'react';
import axios from 'axios';
import { Select, Button, Input, Form, Card, Alert, Space, Divider, Spin, Tag, Collapse } from 'antd';
import { ThunderboltOutlined, PlusOutlined, CloseOutlined, LinkOutlined } from '@ant-design/icons';

const { TextArea } = Input;
const { Panel } = Collapse;

const ParamInput = ({ params, onAddParam, onParamChange, onRemoveParam }) => (
  <div style={{ marginBottom: 16 }}>
    <Space direction="vertical" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 'bold', color: '#2c3e50' }}>请求参数</span>
        <Button type="dashed" onClick={onAddParam} icon={<PlusOutlined />} size="small">添加</Button>
      </div>
      {params.map((param, index) => (
        <div key={index} style={{ display: 'flex', gap: 8, padding: 8, background: '#f8f9fa', borderRadius: 6 }}>
          <Input
            placeholder="参数名"
            value={param.key}
            onChange={(e) => onParamChange(index, 'key', e.target.value)}
            style={{ flex: 1 }}
          />
          <Input
            placeholder="参数值"
            value={param.value}
            onChange={(e) => onParamChange(index, 'value', e.target.value)}
            style={{ flex: 1 }}
          />
          <Button danger onClick={() => onRemoveParam(index)} icon={<CloseOutlined />} size="small" />
        </div>
      ))}
    </Space>
  </div>
);

const ResponseDisplay = ({ response }) => {
  if (!response) return null;

  const extractUrls = (text) => {
    const urlRegex = /https:\/\/[^\s"']+/g;
    return text.match(urlRegex) || [];
  };

  const urls = typeof response === 'string' ? extractUrls(response) : [];
  const isHTML = typeof response === 'string' && /<\/?[a-z][\s\S]*>/i.test(response);

  return (
    <Card title="响应结果" style={{ marginTop: 16 }}>
      <Collapse>
        <Panel header="查看响应" key="1">
          {isHTML ? (
            <div dangerouslySetInnerHTML={{ __html: response }} style={{ maxHeight: '50vh', overflow: 'auto', padding: 16, border: '1px solid #e8e8e8', borderRadius: 4 }} />
          ) : (
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#f8f9fa', padding: 16, borderRadius: 4, border: '1px solid #e8e8e8', maxHeight: '50vh', overflow: 'auto' }}>
              {response}
            </pre>
          )}

          {urls.length > 0 && (
            <div style={{ marginTop: 16 }}>
              {urls.map((url, index) => (
                <Button key={index} type="primary" onClick={() => window.open(url, '_blank')} icon={<LinkOutlined />} style={{ marginRight: 8 }}>
                  访问链接 {index + 1}
                </Button>
              ))}
            </div>
          )}
        </Panel>
      </Collapse>
    </Card>
  );
};

const APISection = ({ apiConfig }) => {
  const [params, setParams] = useState(apiConfig.defaultParams?.length ? apiConfig.defaultParams : [{ key: '', value: '' }]);
  const [method, setMethod] = useState('GET');
  const [body, setBody] = useState('');
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [jsonError, setJsonError] = useState('');

  const validateJSON = (value) => {
    try {
      if (value.trim()) JSON.parse(value);
      setJsonError('');
    } catch {
      setJsonError('无效的 JSON 格式');
    }
  };

  const buildQueryParams = () => {
    return params.filter(p => p.key.trim()).map(p => `${encodeURIComponent(p.key)}=${encodeURIComponent(p.value)}`).join('&');
  };

  const buildTargetUrl = () => {
    const queryString = buildQueryParams();
    return queryString ? `${apiConfig.url}?${queryString}` : apiConfig.url;
  };

  const handleSubmit = async () => {
    setLoading(true);
    setResponse('');

    try {
      const targetUrl = buildTargetUrl();
      let requestData;

      if (method === 'POST' && body.trim()) {
        if (jsonError) throw new Error('请修正 JSON 格式');
        requestData = JSON.parse(body);
      }

      const { data } = await axios({
        method,
        url: `https://szyd.xyz:5000/api/proxy/${encodeURIComponent(targetUrl)}`,
        data: requestData,
        headers: { 'Content-Type': 'application/json' }
      });

      setResponse(typeof data === 'object' ? JSON.stringify(data, null, 2) : data);
    } catch (error) {
      setResponse(<Alert message={`请求失败: ${error.message}`} type="error" showIcon closable />);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      title={<><ThunderboltOutlined style={{ color: '#1890ff' }} /> {apiConfig.title} <Tag color="blue">{apiConfig.url}</Tag></>}
      style={{ marginBottom: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', borderRadius: 8 }}
      extra={
        <Select value={method} onChange={setMethod} style={{ width: 100 }} bordered={false}>
          <Select.Option value="GET">GET</Select.Option>
          <Select.Option value="POST">POST</Select.Option>
        </Select>
      }
    >
      <Divider orientation="left">接口说明</Divider>
      <p style={{ color: '#666', marginBottom: 24 }}>{apiConfig.description}</p>

      <ParamInput
        params={params}
        onAddParam={() => setParams([...params, { key: '', value: '' }])}
        onParamChange={(index, field, value) => {
          const newParams = [...params];
          newParams[index][field] = value;
          setParams(newParams);
        }}
        onRemoveParam={(index) => setParams(params.filter((_, i) => i !== index))}
      />

      {method === 'POST' && (
        <Form.Item label="请求体（JSON）" validateStatus={jsonError ? 'error' : ''} help={jsonError}>
          <TextArea rows={5} value={body} onChange={(e) => { setBody(e.target.value); validateJSON(e.target.value); }} style={{ fontFamily: 'monospace', borderColor: jsonError ? '#ff4d4f' : '#d9d9d9' }} />
        </Form.Item>
      )}

      <Button type="primary" onClick={handleSubmit} loading={loading} block size="large" style={{ backgroundColor: '#1890ff', margin: '16px 0' }}>
        🚀 发送请求
      </Button>

      <Spin spinning={loading} tip="正在请求，请稍候...">
        <ResponseDisplay response={response} />
      </Spin>
    </Card>
  );
};

export default APISection;
